import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import styled from 'styled-components'

import {
  ExclamationCircleOutlined,
  CaretRightOutlined
} from '@ant-design/icons'
import {
  Button,
  Col,
  Collapse,
  Empty,
  Form,
  Input,
  List,
  Modal,
  Row,
  Typography
} from 'antd'
import { FlexSectionHeader } from '../style'

import { STUDENT } from '../../constants/userRoles'

import {
  createCourse,
  getAllCourses,
  deleteCourse,
  unEnroll
} from '../../reducers/courseReducer'
import Spinner from '../../components/Spinner'
import CourseCard from '../../components/CourseCard'
import { useNavigate } from 'react-router'
import { getAllDeadlines } from '../../reducers/deadlinesReducer'
import { DateTime } from 'luxon'

import { FileTextOutlined } from '@ant-design/icons'
import { AiOutlineSolution } from 'react-icons/ai'

const { Title, Text } = Typography
const { confirm } = Modal

const Dashboard = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [filter, setFilter] = useState('')

  useEffect(() => {
    dispatch(getAllCourses('published'))
    dispatch(getAllDeadlines())
  }, [dispatch])

  const user = useSelector((state) => state.auth.user)
  const courses = useSelector((state) =>
    Array.isArray(state.courses.data)
      ? state.courses.data.filter((course) => course.status !== 'archived')
      : []
  )
  const loading = useSelector((state) => state.courses.loading)
  const deadlines = useSelector((state) => state.deadlines.data)
  const deadlinesLoading = useSelector((state) => state.deadlines.loading)

  const enrolledCourses = courses.filter((course) => course.enrolled)

  const filteredCourses = enrolledCourses?.filter((course) => {
    const courseName = course?.name || ''
    const searchFilter = filter || ''
    return courseName.toLowerCase().includes(searchFilter.toLowerCase())
  })

  const handleSearch = (value) => {
    setFilter(value)
  }

  const removeCourse = (courseId) => {
    confirm({
      title: 'Are you sure you want to delete this course?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        dispatch(deleteCourse(courseId))
      },
      onCancel() {
        return
      }
    })
  }

  const handleUnenroll = (courseId, userId) => {
    confirm({
      title: 'Do you Want to unenroll in this course?',
      icon: <ExclamationCircleOutlined />,
      content: 'You will lose access to this course after unenrolling.',
      onOk() {
        dispatch(unEnroll(courseId, userId))
      },
      onCancel() {
        return
      }
    })
  }

  const handleCourseCardClick = (courseId) => {
    navigate(`/app/course/${courseId}/modules`)
  }

  const reviewCourse = (courseId) => {
    navigate(`/app/course/${courseId}/modules`)
  }

  if (loading) return <Spinner size="large" />

  return (
    <>
      <FlexSectionHeader>
        <Title level={3}>Dashboard</Title>
        <Input.Search
          allowClear
          value={filter}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="Enter course name"
          size="large"
          style={{ width: '300px', alignSelf: 'center' }}
        />
      </FlexSectionHeader>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={24} md={24} lg={16} xl={19}>
          <div style={{ marginTop: '8px' }}>
            <Collapse
              expandIcon={({ isActive }) => (
                <CaretRightOutlined rotate={isActive ? 90 : 0} />
              )}
              defaultActiveKey={['1']}
              ghost
            >
              <Collapse.Panel header={<Text strong>My Courses</Text>} key="1">
                <List
                  grid={{
                    gutter: 24,
                    column: 2,
                    xs: 1,
                    sm: 2,
                    xxl: 4
                  }}
                  dataSource={filteredCourses}
                  renderItem={(course) => (
                    <List.Item>
                      <CourseCard
                        course={course}
                        removeCourse={() => removeCourse(course.id)}
                        reviewCourse={() => reviewCourse(course.id)}
                        handleUnenroll={() =>
                          handleUnenroll(course.id, user._id)
                        }
                        onClick={() => handleCourseCardClick(course.id)}
                      />
                    </List.Item>
                  )}
                />
              </Collapse.Panel>
            </Collapse>
          </div>
        </Col>

        <Col xs={24} sm={24} md={24} lg={8} xl={5}>
          <DeadlinesViewer loading={deadlinesLoading} deadlines={deadlines} />
        </Col>
      </Row>
    </>
  )
}

const DeadLinesContainer = styled.div`
  width: 100%;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  border: 0.1px solid #e0d5d5;
  border-radius: 5px;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
  margin-top: 50px;
`

const DeadlineItem = styled(List.Item)`
  background-color: #fafafa;
  padding: 20px;
`

const DeadlinesViewer = (props) => {
  const { loading, deadlines } = props

  if (loading) return <Spinner size="large" />

  const upcomingDeadlines = deadlines
    .filter(
      (item) =>
        DateTime.fromISO(item.deadline, { zone: 'utc' }).toMillis() >=
        DateTime.now().toUTC().toMillis()
    )
    .sort(
      (a, b) =>
        DateTime.fromISO(a.deadline, { zone: 'utc' }) -
        DateTime.fromISO(b.deadline, { zone: 'utc' })
    )

  return (
    <DeadLinesContainer>
      <div
        style={{
          height: '60px',
          backgroundImage:
            'linear-gradient(rgba(0, 109, 117, 1), rgba(0, 109, 117, 0.3))',
          color: 'white',
          padding: '15px 20px'
        }}
      >
        <Typography.Title style={{ color: 'white' }} level={4}>
          Upcoming Deadlines
        </Typography.Title>
      </div>
      <List
        style={{ maxHeight: '380px', overflow: 'hidden', overflowY: 'auto' }}
        dataSource={upcomingDeadlines}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={'No Deadlines'}
            />
          )
        }}
        renderItem={(item) => {
          const date = DateTime.fromISO(item.deadline)
          const handleClick = (e) => {
            e.preventDefault()
            if (item.type === 'Exam') {
              if (item.status?.code === 'closed') {
                Modal.info({
                  title: 'Exam has ended',
                  content:
                    'This exam is closed and cannot be accessed anymore.',
                  okText: 'Ok'
                })
              } else {
                window.open(
                  `/app/course/${item.course.id}/exam/${item.assessmentId}`,
                  '_blank'
                )
              }
            } else {
              window.location.href = `/app/course/${
                item.course.id
              }/${item.type.toLowerCase()}/${item.assessmentId}`
            }
          }
          return (
            <DeadlineItem style={{ padding: '10px' }}>
              <List.Item.Meta
                style={{ alignItems: 'center', paddingBottom: '0px' }}
                avatar={
                  item.type === 'Exam' ? (
                    <AiOutlineSolution style={{ fontSize: '24px' }} />
                  ) : (
                    <FileTextOutlined />
                  )
                }
                title={
                  <a href="#" onClick={handleClick}>
                    {item.title}
                  </a>
                }
                description={item.course.name}
              />
              <Text style={{ width: '80px' }} type="secondary">
                {date.toLocaleString(DateTime.DATETIME_MED)}
              </Text>
            </DeadlineItem>
          )
        }}
      />
    </DeadLinesContainer>
  )
}

export default Dashboard
